import { useState } from 'react';
import { Check, Loader2, Save } from 'lucide-react';

/**
 * The save-state indicator in the focus-mode header: a small pill that
 * reports whether the document has unsaved changes, and doubles as a
 * save control.
 *
 * Crucially, this saves a **draft**, not a publication. Those are
 * different things and the copy has to keep them distinct:
 *
 *  - *Unsaved changes* exist only in memory — closing the tab loses them.
 *  - *Saved draft* is stored in this browser's localStorage only. It is
 *    not on Nostr, not signed, not visible to anyone else, and is lost
 *    if the browser's site data is cleared.
 *  - *Published* is a signed event on relays — the only state other
 *    people can see.
 *
 * The badge therefore says "Save draft" on hover — never just "Save",
 * which would risk reading as "publish" or "commit". After saving it
 * reports "Saved locally" rather than "Saved", so a reader glancing at
 * it later doesn't mistake a local draft for published work.
 */

export type SaveState = 'idle' | 'dirty' | 'saving' | 'saved';

interface SaveStatusBadgeProps {
  state: SaveState;
  /** Called when the user clicks the badge while it's in the dirty state. */
  onSave: () => void;
}

export function SaveStatusBadge({ state, onSave }: SaveStatusBadgeProps) {
  // Hover swaps the dirty badge's label to the action it performs —
  // "Save draft" — so a click is never a surprise. This is a one-way
  // affordance: only the dirty state is interactive; the others are
  // read-only status.
  const [hovered, setHovered] = useState(false);

  if (state === 'idle') return null;

  if (state === 'saving') {
    return (
      <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        Saving…
      </span>
    );
  }

  if (state === 'saved') {
    return (
      <span
        className="flex shrink-0 items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"
        title="Stored as a draft in this browser only — not published."
      >
        <Check className="size-3" />
        Saved locally
      </span>
    );
  }

  // dirty — the only interactive state.
  return (
    <button
      type="button"
      onClick={onSave}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title="Save a draft to this browser (not published to Nostr)"
      className="flex shrink-0 items-center gap-1.5 rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-500/25 dark:text-amber-500"
    >
      <Save className="size-3" />
      {hovered ? 'Save draft' : 'Unsaved changes'}
    </button>
  );
}
